import { FC, useState } from "react";
import { useSnapshot } from "valtio";
import Loader from "src/components/common/loader/loader";
import { launchStore } from "src/modules/engagement/launchStore";
import { CreateAudienceModal } from "./CreateAudienceModal";
import "./audience.scss";

/** A rule group is nested; the leaf count is what "how targeted is this" means. */
function countRules(rules: any): number {
  if (!rules || typeof rules !== "object" || !Array.isArray(rules.rules)) return 0;
  return rules.rules.reduce(
    (total: number, rule: any) => total + (Array.isArray(rule?.rules) ? countRules(rule) : 1),
    0,
  );
}

interface Props {
  engagementId: number;
  onOpen: (audienceId: number) => void;
}

const AudienceListView: FC<Props> = ({ engagementId, onOpen }) => {
  const { audiences, isLoadingAudiences } = useSnapshot(launchStore);

  const [showCreate, setShowCreate] = useState(false);

  if (isLoadingAudiences) return <Loader />;

  const createModal = (
    <CreateAudienceModal
      show={showCreate}
      onClose={() => setShowCreate(false)}
      engagementId={engagementId}
    />
  );

  if (audiences.length === 0) {
    return (
      <>
        <div className="audience__empty">
          <div className="audience__empty-title">No audiences yet</div>
          <div className="audience__empty-copy">
            An audience is a slice of the population this engagement targets, defined by rules
            over the data you've loaded.
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => setShowCreate(true)}
            style={{ fontSize: "0.72rem" }}
          >
            Create audience
          </button>
        </div>
        {createModal}
      </>
    );
  }

  return (
    <>
      <div className="audience__section">
        <div className="audience__head">
          <i className="bi bi-person-lines-fill" style={{ color: "#64748b" }} />
          <span className="audience__title">Audiences</span>
          <span className="audience__count">{audiences.length}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary ms-auto"
            onClick={() => setShowCreate(true)}
            style={{ fontSize: "0.72rem" }}
          >
            New audience
          </button>
        </div>

        {/*
            One row per audience, stacked. Tiled cards read fine at three but
            turn into a wall you have to scan in two directions once an
            engagement has a dozen — and the counts stopped lining up, which is
            the whole reason to look at them together.
        */}
        <div className="audience-list">
          {audiences.map((audience) => {
            const ruleCount = countRules(audience.rules);

            return (
              <button
                key={audience.id}
                type="button"
                className="audience-row"
                onClick={() => onOpen(audience.id)}
              >
                <span className="audience-row__main">
                  <span className="audience-row__name">{audience.name}</span>
                  <span className="audience-row__meta">
                    <span className="audience-row__muted">
                      {ruleCount === 0
                        ? "No rules yet — everyone in the population"
                        : `${ruleCount} ${ruleCount === 1 ? "rule" : "rules"}`}
                    </span>
                  </span>
                </span>

                {/* Fixed widths so the numbers form columns down the list. */}
                <span className="audience-row__stats">
                  <span className="audience-row__stat">
                    <span className="audience-row__stat-label">Members</span>
                    <span className="audience-row__stat-value">
                      {(audience.total_members ?? 0).toLocaleString()}
                    </span>
                  </span>
                  <span className="audience-row__stat">
                    <span className="audience-row__stat-label">Active</span>
                    <span className="audience-row__stat-value">
                      {(audience.total_targetable ?? 0).toLocaleString()}
                    </span>
                  </span>
                  <span className="audience-row__stat">
                    <span className="audience-row__stat-label">Converted</span>
                    <span className="audience-row__stat-value">
                      {(audience.total_converted ?? 0).toLocaleString()}
                    </span>
                  </span>
                </span>

                <span className="audience-row__chev">›</span>
              </button>
            );
          })}
        </div>
      </div>
      {createModal}
    </>
  );
};

export default AudienceListView;
